import { Canvas } from '@react-three/fiber'
import { Suspense } from 'react';
import { BaseFigure as Model, FigureOverlay, ModelLoading } from '../../components/';
import { Center, CameraControls } from '@react-three/drei';
import { theme } from '../../theme';
import { useState, useRef } from 'react';
import * as THREE from 'three'

const CURVE_POINTS = 200;
const Sphere = ({ radius = 1, segments = 20, cylinderRadius = 0.75, cylinderHeight = 1.25, highlightFigure = 0 }) => {
  const cylinderRef = useRef(null);
  const bottomRef = useRef(null);
  const meridians = [];
  // Meridians
  for (let i = 0; i < segments; i++) {
    const rotation = Math.PI * (i / segments);
    const curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      radius, radius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    meridians.push({ g: new THREE.BufferGeometry().setFromPoints(points), r: rotation });
  }
  // Parallels
  let parallels = [];
  for (let i = 0; i < segments; i++) {
    let pOffset = radius - radius / Math.pow(2, i);
    let pRadius = Math.sqrt(radius * radius - pOffset * pOffset);
    let curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      pRadius, pRadius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    parallels.push({ g: new THREE.BufferGeometry().setFromPoints(points), p: -pOffset });
  }
  const material = new THREE.LineBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.1});

  // Cylinder
  const cylinderMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    uniforms: {
      vlak3color1: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: `
      varying vec3 vUv; 

      void main() {
        vUv = position; 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 vlak3color1;
      varying vec3 vUv;
     
      void main() {
        // y < 0 = transparent, > 1 = opaque
        float alpha = smoothstep(-0.5, 1.0, -vUv.y);

        gl_FragColor = vec4(vlak3color1, alpha);
      }
    `, 
  });
  // Cylinder Bottom
  const cylinderBottomMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    uniforms: {
      vlak3color1: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: `
      varying vec3 vUv; 

      void main() {
        vUv = position; 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 vlak3color1;
      varying vec3 vUv;
     
      void main() {
        // y < 0 = transparent, > 1 = opaque
        float alpha = smoothstep(1.0, -1.0, -vUv.y);

        //gl_FragColor = vec4(vlak3color1, alpha);
        gl_FragColor = vec4(vlak3color1, 0.74);
      }
    `, 
  });

  const cylinderGeometry = new THREE.CylinderGeometry(cylinderRadius, cylinderRadius, cylinderHeight, 64, 1, true);
  const bottomGeometry = new THREE.CircleGeometry(cylinderRadius, 64);
  const highlightPositions = [
    {x: 0, z: 0},
    {x: 1, z: 0},
    {x: -0.5, z: 0.75},
    {x: -0.5, z: -0.75},
    {x: 0.5, z: 0.75},
    {x: 0.5, z: -0.75},
    {x: -1, z: 0}
  ]
  return (
    <mesh position={[0, radius - 0.01, 0]} castShadow>
      {meridians.map(({ g, r }, i) => (<line key={`mer-${i}`} rotation={[0, r, 0]} args={[g, material]} />))}
      {parallels.map(({ g, p }, i) => (<line key={`par-${i}`} position={[0, p, 0]} rotation={[Math.PI / 2, 0, 0]} args={[g, material]} />))}
      <mesh ref={cylinderRef} position={[highlightPositions[highlightFigure].x, -radius + cylinderHeight/2 + 0.02, highlightPositions[highlightFigure].z]} args={[cylinderGeometry, cylinderMaterial]} />
      <mesh ref={bottomRef} position={[highlightPositions[highlightFigure].x, -radius + 0.02, highlightPositions[highlightFigure].z]} rotation={[Math.PI / 2, 0, 0]} args={[bottomGeometry, cylinderBottomMaterial]} />
    </mesh>
  );
};

const Scene = ({ controls = null, animationType, figure1, figure2, figure3, figure4, figure5, figure6, cylinderRadius = 0.75, cylinderHeight = 1.25, selectedIndex = 1 }) => {
  const sphereRadius = 64;
  return (
    <>
      <ambientLight intensity={1} />
      <Suspense fallback={<FigureOverlay customStyle={{top: '-100px', left: '-120px'}}><ModelLoading /></FigureOverlay>}>
        <Center bottom position={[0, sphereRadius * 2 - 1, 0]}>
          <Sphere radius={sphereRadius} cylinderRadius={cylinderRadius} cylinderHeight={cylinderHeight} highlightFigure={selectedIndex}/>
        </Center>
        <Model controls={controls} animationType={animationType} key={`figure-${figure1.getId()}`} scale={figure1.getScaleFactor()} glbPath={figure1.getVrmSource()} rotation={[0, -Math.PI/2, 0]} position={[1, -1, 0]}  armorGlbPath='/assets/arms.vrm' weapons={[{ weaponGlbPath: '/assets/launcher.glb', weaponType: 'heavy', effects: [] }]}/>
        <Model controls={controls} animationType={animationType} key={`figure-${figure2.getId()}`} scale={figure2.getScaleFactor()} glbPath={figure2.getVrmSource()} rotation={[0, 2 * Math.PI/3, 0]} position={[-0.5, -1, 0.75]} droneGlbPath={null}/>
        <Model controls={controls} animationType={animationType} key={`figure-${figure3.getId()}`} scale={figure3.getScaleFactor()} glbPath={figure3.getVrmSource()} rotation={[0, 0, 0]} position={[-0.5, -1, -0.75]} objectGlbPath={null} droneGlbPath={'/assets/boombox.glb'} droneType='dance'/>
      </Suspense>
    </>
  );
}

const DECORATORS_THRESHOLD_MIN = 8;
const DECORATORS_THRESHOLD_MAX = 11;
const MOVEMENT_POSITIONS = {
  'home': {
    target: { x: -0.34, y: -0.10, z: 0.14 },
    position: { x: -3.37, y: 0.83, z: -7.08 }
  },
  'home_collapsed': {
    target: { x: 0.27, y: -0.16, z: -0.1 },
    position: { x: -2.48, y: 1.24, z: -7.35 }
  },
  'figure1': {
    target: { x: 0.0014424697570170368, y: -0.36336777318661007, z: 0.9337784079461453 },
    position: { x: 10.51549858710874, y: 1.9629322268133906, z: 2.53150916473178 }
  },
  'figure2': {
    target: { x: -0.5073019202602745, y: -0.2785825810716795, z: -0.45478844451446543 },
    position: { x: -7.781400276185373, y: 1.0593881285005322, z: 7.533503523024942 }
  },
  'figure3': {
    target: { x: 0.8103424227806607, y: -0.2827288149842471, z: -0.22567096309626392 },
    position: { x: -4.46791651427217, y: 1.2135711850157533, z: -9.62848174569855 }
  },
  'center': {
    target: { x: 0, y: -0.15, z: 0 },
    position: { x: -2.75, y: 1.25, z: -7.25 }
  }
};

export default function FigureView({ figure1, figure2, figure3, figure4, figure5, figure6, animationType, selectedIndex, width, height, noDecorators = false, controls = null, highlightedIndex = 0}) {
  const [dragging, setDragging] = useState(false);

  const [camera, setCamera] = useState(null);
  const sceneControlsRef = useRef(null);
  const figureControlsRef = useRef(null);
  const lightHolderRef = useRef(null);
  const cylinderRadius = 0.65;
  const cylinderHeight = 1;

  const [enabledControls, setEnableControls] = useState(true);

  const moveSmoothly = (newPosition, notSmooth = false) => {
    if (!figureControlsRef.current) return;
    const {target, position} = newPosition;
    figureControlsRef.current.setTarget(target.x, target.y, target.z, !notSmooth);
    figureControlsRef.current.setPosition(position.x, position.y, position.z, !notSmooth);
  }

  const homeAvatar = (collapsed = false) => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS[ collapsed ? 'home_collapsed' : 'home']);
    }
  }

  const selectFigure1 = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure1']);
    }
  }

  const selectFigure2 = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure2']);
    }
  }

  const selectFigure3 = () => {
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['figure3']);
    }
  }

  if (controls) {
    controls.homeAvatar = homeAvatar;
    controls.selectFigure1 = selectFigure1;
    controls.selectFigure2 = selectFigure2;
    controls.selectFigure3 = selectFigure3;
  }
  /*
  useEffect(() => {
    if (figureControlsRef.current) {
      figureControlsRef.current.addEventListener('update', (e) => {
        const target = new THREE.Vector3(0, 0, 0);
        const position = new THREE.Vector3(0, 0, 0);
        figureControlsRef.current.getTarget(target, false);
        figureControlsRef.current.getPosition(position, false);
      });
    }
  }, [figureControlsRef.current]);
  */
  return (
    <div onMouseUp={() => setDragging(false)} onMouseDown={() => setDragging(true)} style={{cursor: dragging ? 'grabbing' : 'grab'}}>
      <Canvas gl={{antialias: true, logarithmicDepthBuffer: true}} shadows={true} onCreated={() => setTimeout(() => selectFigure1(), 100)} style={{ zIndex: 0, position: 'absolute', top: 0, left: 0, backgroundColor: theme.lightBlue, width: width, height }} shadows camera={{position:[-2.48, 1.24, -7.35], fov: 15 }}>
        <Scene animationType={animationType} controls={controls} selectedIndex={selectedIndex} figure1={figure1} figure2={figure2} figure3={figure3} figure4={figure4} figure5={figure5} figure6={figure6} cylinderRadius={cylinderRadius} cylinderHeight={cylinderHeight} />
	      <CameraControls ref={figureControlsRef} mouseButtons={{ left: 1, wheel: 0, right: 2 }} dollyToCursor={true} minDistance={1} maxDistance={100}/>
      </Canvas>
    </div>
  );
}
